import { LDEvaluationDetail, LDFlagValue, LDLogger, LDSingleKindContext } from 'launchdarkly-js-sdk-common'
import { LDReactOptions, ProviderConfig, asyncWithLDProvider, useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk'
import React, { FunctionComponent, useEffect } from 'react'
import { LaunchDarklyContextProps, LaunchDarklyUserProps } from './types'
import { LDClient, LDOptions } from 'launchdarkly-js-client-sdk'

const DEFAULT_LAUNCH_DARKLY_CONFIG: {
  options: LDOptions
  reactOptions: LDReactOptions
} = {
  options: {
    bootstrap: 'localStorage',
    privateAttributes: ['email', 'consumerEmail', 'consumerPhone'],
    fetchGoals: false,
    evaluationReasons: true,
    sendEvents: true,
  },
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
}

export const getCurrentUserContextFromClient = (client: LDClient | undefined): LDSingleKindContext | null => {
  if (typeof client === 'undefined') {
    return null
  }
  const context = client.getContext()

  if (context && 'kind' in context && context.kind !== 'undefined') {
    if (context.kind === 'user') {
      return context
    }

    return context.user
  }

  return null
}

export function useLaunchDarklyUser(userContext: LaunchDarklyUserProps): LDSingleKindContext {
  const client = useLDClient()

  const { uuid, country, email, merchantId, ...otherProps } = userContext

  const currentUser = getCurrentUserContextFromClient(client)
  // There are times that stale data can set the SDK user as the previous session's on first load (iframe only)
  // If uuid is undefined, then we know that the user is not logged in.
  // Here we can return anonymous: true as opposed to relying on the SDK's LD user, because the SDK is incorrect now.
  // Because now the SDK's LD user is out of sync with our LD user, we identify (sync) again in the useEffect..
  // .. in the LaunchDarklyUser component.
  // TLDR: in iframe mode, we will have to sync twice to ensure that the SDK is in sync with the app state.

  if (typeof uuid === 'undefined') {
    return {
      ...otherProps,
      kind: 'user',
      key: currentUser?.key ?? undefined,
      anonymous: typeof currentUser === 'undefined',
      preLogin: true,
      country,
      merchantId: merchantId ?? undefined,
    }
  }

  // If is logged in
  if (currentUser !== null && currentUser?.key === uuid) {
    return {
      ...(country != null && { country }),
      ...(merchantId != null && { merchantId }),
      ...currentUser,
      ...otherProps,
    }
  }

  return {
    ...otherProps,
    kind: 'user',
    key: uuid,
    ...(country != null && { country }),
    ...(email != null && { email }),
    ...(merchantId != null && { merchantId }),
  }
}

export const useIsLDClientDefined = (): boolean => {
  const client = useLDClient()

  return typeof client !== 'undefined'
}

export const useWaitForLDClientInitialisation = (): (() => Promise<void>) => {
  const ldClient = useLDClient()

  const waitForLDInitialise = async (): Promise<void> => {
    try {
      await ldClient?.waitForInitialization()
    } catch (e) {
      console.error('LD Client failed to initialise', e)
    }
  }

  return waitForLDInitialise
}

export const getLaunchDarklyConfiguration = (config: Partial<ProviderConfig> & Pick<ProviderConfig, 'clientSideID'>): ProviderConfig => {
  return {
    clientSideID: config.clientSideID,
    options: {
      ...DEFAULT_LAUNCH_DARKLY_CONFIG.options,
      ...config.options,
    },
    reactOptions: {
      ...DEFAULT_LAUNCH_DARKLY_CONFIG.reactOptions,
      ...config.reactOptions,
    },
  }
}

export const LaunchDarklyContext: FunctionComponent<LaunchDarklyContextProps> = ({ userProps, children, onLoad = () => {}, onDone = () => {} }) => {
  const user = useLaunchDarklyUser(userProps)
  const client = useLDClient()
  const isClientDefined = useIsLDClientDefined()

  const userFromClient = getCurrentUserContextFromClient(client)
  useEffect(() => {
    if (isClientDefined && typeof client !== 'undefined') {
      const isIdentificationRequired =
        user?.key !== userFromClient?.key || user?.country !== userFromClient?.country || user?.merchantId !== userFromClient?.merchantId
      if (!isIdentificationRequired) {
        onDone()
      } else {
        onLoad()
        // syncing app LD state with SDK LD state
        client.identify(user, undefined, onDone)?.catch(console.info)
      }
    }
  }, [
    client,
    isClientDefined,
    user,
    onDone,
    onLoad,
    user?.country,
    user?.key,
    userFromClient?.country,
    userFromClient?.key,
    userFromClient?.merchantId,
    user?.merchantId,
  ])

  useEffect(() => {
    // Surpress LD errors causing DD sessions to crash
    // Ref: https://support.launchdarkly.com/hc/en-us/articles/12998125691419-Error-LaunchDarklyFlagFetchError-network-error-
    client?.on('error', (e) => {
      /* {do nothing} */
    })
  }, [client])

  return <>{children}</>
}

export function useFlag(flag: string): boolean
export function useFlag<T>(flag: string, defaultValue: T): T

export function useFlag<T>(flag: string, defaultValue?: T): T | boolean {
  const client = useLDClient()
  const value = client?.variation(flag)

  return (value as T) ?? defaultValue ?? false
}

export { useLDClient, withLDProvider, asyncWithLDProvider }
export type { LaunchDarklyContextProps, LDLogger, LDEvaluationDetail, LDFlagValue, LDSingleKindContext }
